import React, { useEffect, useRef, useState } from 'react';

import { useInView, useMediaQuery } from 'hooks';
import { Button, ButtonVariant, InvertedBackground, SectionContainer } from 'ui';
import { LayoutContainer } from 'layout';

import {
    grid,
    knowledgeCardContainer,
    headline,
    screenTablet,
    sectionContainer,
    buttonContainer,
    layoutContainerOverride
} from './knowledge-hub-section.module.scss';
import { KnowledgeCard } from './KnowledgeCard';
import { knowledgeItems } from './KnowledgeHubData';
import { colorBgInvertedPrimaryOne } from '../../../ui/invertedBackground/inverted-background.module.scss';

const ITEMS_PER_LOAD = 4;

export const KnowledgeHubSection = () => {
    const [visibleItems, setVisibleItems] = useState(ITEMS_PER_LOAD);
    const [isInView, setIsInView] = useState(false);
    const visibleCards = knowledgeItems.slice(0, visibleItems);
    const isTablet = useMediaQuery(`(min-width: ${screenTablet})`);
    const buttonRef = useRef<HTMLDivElement>(null);
    const knowledgeRef = useRef<HTMLDivElement>(null);
    const knowledgeObserved = useInView(knowledgeRef, { threshold: 0.3 });

    const loadMore = () => {
        setVisibleItems(prevVisibleItems => prevVisibleItems + ITEMS_PER_LOAD);
    };

    const reset = () => {
        setVisibleItems(ITEMS_PER_LOAD);

        setTimeout(() => {
            if (buttonRef.current) {
                buttonRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
            }
        });
    };

    useEffect(() => {
        const initialVisibleItems = !isTablet ? ITEMS_PER_LOAD : knowledgeItems.length;
        setVisibleItems(initialVisibleItems);
    }, [isTablet]);

    useEffect(() => {
        const sectionIsInView = knowledgeObserved as boolean;

        if (sectionIsInView && !isInView) {
            setIsInView(true);
        }
    }, [knowledgeObserved, isInView]);

    return (
        <InvertedBackground color={colorBgInvertedPrimaryOne}>
            <LayoutContainer className={layoutContainerOverride}>
                <SectionContainer className={sectionContainer} ref={knowledgeRef}>
                    <h3 className={headline}>
                        Elumity is your personalized learning space for keeping and growing knowledge. Lifelong.
                    </h3>
                    <h5>
                        Discover some comprehensive features that will make Elumity your favorite academic productivity
                        suite.
                    </h5>
                    <div className={grid}>
                        {visibleCards.map((knowledgeCard, i) => (
                            <div
                                className={knowledgeCardContainer}
                                key={i}
                                style={isInView ? { opacity: 1, transform: 'translateY(0)' } : {}}>
                                <KnowledgeCard {...knowledgeCard} />
                            </div>
                        ))}
                    </div>
                    {!isTablet && (
                        <div className={buttonContainer} ref={buttonRef}>
                            <Button
                                text={visibleCards.length === knowledgeItems.length ? 'Show less' : 'Show more'}
                                variant={ButtonVariant.Outline}
                                fullWidth
                                onClick={visibleCards.length === knowledgeItems.length ? reset : loadMore}
                            />
                        </div>
                    )}
                </SectionContainer>
            </LayoutContainer>
        </InvertedBackground>
    );
};
